/**
 * The theme context and `useTheme`.
 *
 * The DEFAULT value is the light palette with the feature off. That is deliberate:
 * anything rendered without a `ThemeProvider` — the legacy web app, a shared
 * component on a web page, every existing jest test — gets exactly today's colours.
 * A provider is only needed to make something dark.
 */
import { createContext, useContext } from 'react';
import { light, type Palette } from './palette';
import { DEFAULT_PREFERENCE, type Preference, type Scheme } from './resolveScheme';

export interface ThemeContextValue {
  /** The theme being rendered. */
  scheme: Scheme;
  /** The colours for that theme. */
  palette: Palette;
  /** What the athlete chose. Kept even while the feature is off. */
  preference: Preference;
  /** Save a new choice. Resolves once it is stored. */
  setPreference: (next: Preference) => Promise<void>;
  /** The `dark_mode_enabled` flag. False ⇒ `scheme` is always light. */
  enabled: boolean;
  /** False until the saved choice has been read from storage at launch. */
  hydrated: boolean;
}

export const DEFAULT_THEME: ThemeContextValue = {
  scheme: 'light',
  palette: light,
  preference: DEFAULT_PREFERENCE,
  // Nothing to persist without a provider. Not an error: a screen rendered outside
  // one (web, a test) has no picker to call this from.
  setPreference: async () => {},
  enabled: false,
  hydrated: true,
};

export const ThemeContext = createContext<ThemeContextValue>(DEFAULT_THEME);

/** The current theme. Use `palette` for colours passed as props (icons, gradients, SVG). */
export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext);
}
